import type {
  PieChartComponentSpec,
  ProcessedWidgetData,
  WidgetResponseSpec,
  MetricOption,
  SubjectEntity,
} from "../../Processing/types";
import { processWidget } from "../../Processing/pipeline";
import { resolveControllerDisplay, type ControllerOption } from "../../Processing/controllerBindings";
import { resolveSubjectNames } from "../shared/PlayersForm";
import { formStateToSpec } from "./pieState";
import type { PieFormState } from "./pieState";

function seededRandom(seed: number): number {
  const x = Math.sin(seed * 9301 + 49297) * 49297;
  return x - Math.floor(x);
}

export class PiePreviewBuilder {
  constructor(
    private state: PieFormState,
    private metrics: MetricOption[],
    private getSubjectEntities: (type: string) => SubjectEntity[],
    private controllers: ControllerOption[] = [],
  ) {}

  hasValidSlice(): boolean {
    return this.state.slices.some((s) => s.selectedMetricId != null);
  }

  buildSpec(): PieChartComponentSpec | null {
    return formStateToSpec(this.state, this.metrics, this.getSubjectEntities);
  }

  buildMockResponse(spec: PieChartComponentSpec): WidgetResponseSpec {
    return {
      widget_id: "preview",
      series: spec.series.map((s, i) => ({
        query_id: s.queryId,
        name: "preview",
        data: [{
          value: Math.round(200 + seededRandom(i * 137) * 400),
          category: (s.series_builder_misc.selectedSubjects?.[0] ?? s.series_builder_misc.selectedDrills?.[0] ?? `slice-${i}`),
          category_label: s.displayNameOverride?.trim() || s.displayName,
        }],
      })),
    };
  }

  build(): ProcessedWidgetData | null {
    const spec = this.buildSpec();
    if (!spec) return null;
    const response = this.buildMockResponse(spec);
    // The preview labels a bound slice with its controller's subjects/dates, the same way
    // the rendered widget does.
    return processWidget(
      // Re-derive the controller's subject NAMES from its ids here too: the stored names
      // are a cache, and a stale one is what leaves the legend reading "Full Team".
      resolveControllerDisplay(spec, this.controllers, (subjects, drills) =>
        resolveSubjectNames(subjects, drills, this.getSubjectEntities),
        this.getSubjectEntities,
      ),
      response,
    );
  }
}
